import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { User } from "../../../model";
import { SelectServerSearch, SingleSelectSearch } from "../../../components/ui/DropdownSelect";
import { SingleDatePicker } from "../../../components/ui/SingleDatePicker";

interface ManyToManyItem {
  select_id: number;
  from_date?: Date;
  to_date?: Date;
}

function newManyToManyItem(): ManyToManyItem {
  return {
    select_id: 0,
  }
}

interface ManyToManyFormProps {
  item: ManyToManyItem;
  selectItemName: string;
  selectTable: string;
  required?: boolean;
  error?: string;
  fromAndToDate?: boolean;
  onItemChange?: (id: number | null) => void;

}

export interface ManyToManyFormRef {
  valid: () => boolean;
  getItem: () => ManyToManyItem;
}

export const ManyToManyForm = forwardRef((props: ManyToManyFormProps, ref) => {
  const { selectItemName, selectTable, error, required = true, fromAndToDate = false } = props;
  const [item, setItem] = useState<ManyToManyItem>(newManyToManyItem);
  const [submitted, setSubmitted] = useState(false);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  useEffect(() => {
    setItem(props.item);
    setTouched({ base: {} })
    setSubmitted(false);
  }, [props.item]);

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(item) as (keyof ManyToManyItem)[]) {
      if (typeof item[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getItem = () => {
    return item;
  }

  // Expose the valid function to the parent, and a method to get the item
  useImperativeHandle(ref, () => ({
    valid,
    getItem,
  }));

  const validateBaseField = (field: keyof ManyToManyItem): string => {
    if (field === "select_id" && required) {
      if (!item.select_id) return "Venligst velg";
    }
    return ""; // No error
  };

  const handleBaseChange = (field: keyof ManyToManyItem, value: any) => {
    setItem((prev) => ({ ...prev, [field]: value }));

    if (field === "select_id" && props.onItemChange) {
      props.onItemChange(value);
    }

    // setTouched((prev) => ({
    //   ...prev,
    //   base: { ...prev.base, [field]: true },
    // }));
  };

  const handleBaseBlur = (field: keyof ManyToManyItem) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  return (
    <form>
      <Grid container spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        {error && <Alert sx={{ width: '100%' }} severity="error">{error}</Alert>}

        <Grid item xs={12}>
          <Box sx={{ width: "100%" }}>
            <SelectServerSearch
              label={selectItemName}
              tableName={selectTable}
              selected={item.select_id ? [item.select_id] : []}
              required={required}
              singleSelect
              onChange={(ids) => {
                if (ids.length > 0) {
                  handleBaseChange("select_id", ids[0]);
                } else {
                  handleBaseChange("select_id", null);
                }
              }}
              error={required && validateBaseField("select_id") !== "" && (submitted || touched.base.select_id)}
              onBlur={() => handleBaseBlur("select_id")}
            />
          </Box>
        </Grid>
        {fromAndToDate && (
          <>
            <Grid item xs={12} sm={6}>
              <SingleDatePicker
                label="Fra dato"
                fullWidth
                date={item.from_date}
                onChange={(date) => handleBaseChange("from_date", date)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SingleDatePicker
                label="Til dato"
                fullWidth
                date={item.to_date}
                onChange={(date) => handleBaseChange("to_date", date)}
              />
            </Grid>
          </>
        )}
      </Grid>
    </form>
  )
});